import React, { useEffect, useMemo, useState } from "react";
import { useForm } from "react-hook-form";
import { MathJax } from "better-react-mathjax/esm";
import _ from "lodash/fp";

import {
  Box,
  Checkbox,
  FormControlLabel,
  FormGroup,
  IconButton,
  Menu,
  MenuItem,
  Radio,
  RadioGroup,
  ToggleButton,
  ToggleButtonGroup,
  Toolbar,
  Tooltip,
  Typography,
} from "@mui/material";
import { EyeSettings as PreviewMenuIcon } from "@actinc/dls/icons/";
import { useClient } from "urql";
import { StatusCodes } from "http-status-codes";

import { ControlledFormInput } from "..";
import { Loading } from "@actinc/dls/components";
import {
  convertSuggestionsForDisplay,
  transformMathMlMfenced,
} from "../../services/contentUtils";
import {
  lettersArray,
  getLetter,
  RIMS_PREVIEW_ENGINES,
} from "../../services/utils";
import { PreviewContentDialog } from "../../routes/Dashboard/Tasks/PreviewContentDialog";

const DEFAULT_WAIT_TIME = 5000;
const MAX_POLL_ATTEMPTS = 60;

const PreviewContent = ({
  color = "primary",
  compareOptions,
  objects,
  objectToPreview,
  objectType,
  previewTypes = [],
}) => {
  const [anchorEl, setAnchorEl] = useState(null);
  const client = useClient();
  const open = Boolean(anchorEl);

  const [display, setDisplay] = useState("CBT");

  const [loading, setLoading] = useState(true);
  const [content, setContent] = useState();
  const [previewContentDialogProps, setPreviewContentDialogProps] = useState({
    open: false,
  });

  // Fetch the item data
  useEffect(() => {
    if (objectToPreview) {
      _.pipe([
        convertSuggestionsForDisplay,
        transformMathMlMfenced,
        setContent,
      ])(objectToPreview.content);
      setLoading(false);
    }
  }, [objectToPreview]);

  const checkPreviewStatus = async (previewId, attempt = 1) => {
    const response = await client
      .query(previewContentStatusDisplayQuery, {
        action: "STATUS",
        previewId: previewId,
      })
      .toPromise();

    const rimsContent = response?.data?.rimsPreviewContent;
    if (!rimsContent) {
      console.error("Preview status returned no data:", {
        previewId,
        error: response?.error,
      });
      throw new Error(`Preview status unavailable for previewId ${previewId}`);
    }

    const previewRequestStatus = JSON.parse(rimsContent);

    if (previewRequestStatus.status === StatusCodes.ACCEPTED) {
      if (attempt >= MAX_POLL_ATTEMPTS) {
        throw new Error(
          `Preview generation timed out for previewId ${previewId} after ${MAX_POLL_ATTEMPTS} attempts`,
        );
      }
      await new Promise((resolve) => setTimeout(resolve, DEFAULT_WAIT_TIME));
      return checkPreviewStatus(previewId, attempt + 1);
    } else if (previewRequestStatus.status !== StatusCodes.OK) {
      console.error("Preview Generation ERROR:", previewRequestStatus);
    }

    return previewRequestStatus.status;
  };

  const getPreviewContent = async (previewId) => {
    const response = await client
      .query(previewContentStatusDisplayQuery, {
        action: "DISPLAY",
        previewId: previewId,
      })
      .toPromise();

    const rimsContent = response?.data?.rimsPreviewContent;
    if (!rimsContent) {
      console.error("Preview content returned no data:", {
        previewId,
        error: response?.error,
      });
      return { data: null };
    }

    return JSON.parse(rimsContent);
  };

  const parsePreviewRequest = (response) => {
    const rimsContent = response?.data?.rimsPreviewContent;
    if (!rimsContent) {
      console.error("Preview request returned no data:", {
        error: response?.error,
      });
      return null;
    }

    try {
      return JSON.parse(rimsContent);
    } catch (error) {
      console.error("Failed to parse preview request response:", {
        error: error?.message,
      });
      return null;
    }
  };

  const handlePreviewClick = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handlePreviewClose = () => {
    setAnchorEl(null);
  };

  const handleCbtPbtToggle = (e) => {
    setDisplay(e.target.innerText);
  };

  const openPreviewContentDialog = (object) => (_e) => {
    setPreviewContentDialogProps({
      onClose: () => setPreviewContentDialogProps({ open: false }),
      open: true,
      object,
    });
  };

  const pollAndRender = async (previewWindow, previewId, useIframe) => {
    try {
      await checkPreviewStatus(previewId);
      const previewContent = await getPreviewContent(previewId);
      if (previewContent?.data) {
        previewWindow.document.body.replaceChildren();
        if (useIframe) {
          const iframe = previewWindow.document.createElement("iframe");
          iframe.style.width = "100%";
          iframe.style.height = "100%";
          iframe.style.border = "none";

          iframe.srcdoc = previewContent.data;
          previewWindow.document.body.appendChild(iframe);
        } else {
          previewWindow.document.write(previewContent.data);
        }

        // Function to load a script dynamically
        const loadScript = (src, callback) => {
          const script = previewWindow.document.createElement("script");
          script.src = src;
          script.async = true;
          script.onload = callback;
          script.onerror = () => console.error(`Failed to load script: ${src}`);
          previewWindow.document.head.appendChild(script);
        };

        // Load jQuery first, then jQuery UI
        loadScript(
          "https://ajax.googleapis.com/ajax/libs/jquery/3.6.0/jquery.min.js",
          () => {
            loadScript(
              "https://ajax.googleapis.com/ajax/libs/jqueryui/1.13.2/jquery-ui.min.js",
            );
          },
        );
        previewWindow.document.close();
      } else {
        previewWindow.document.body.innerHTML =
          "Error Previewing. Please Try Again!";
      }
    } catch (error) {
      console.error("Preview generation failed:", {
        previewId,
        error: error?.message,
      });
      previewWindow.document.body.innerHTML =
        "Error Previewing. Please Try Again!";
    }
  };

  const handlePreviewMenuClose = (previewEngine) => async (event) => {
    const previewWindow = window.open("/preview-content", "_blank");

    const response = await client
      .query(previewContentQuery, {
        action: "REQUEST",
        objects,
        objectType,
        previewEngine,
      })
      .toPromise();

    const previewRequest = parsePreviewRequest(response);
    if (previewRequest?.status === StatusCodes.ACCEPTED) {
      const previewId = previewRequest.data.data[0].previewId;
      await pollAndRender(
        previewWindow,
        previewId,
        RIMS_PREVIEW_ENGINES.includes(previewEngine),
      );
    } else {
      previewWindow.document.body.innerHTML =
        "Error Creating Preview. Please Try Again!";
    }

    setAnchorEl(null);
  };

  const handleCompareMenuClose =
    ({ left, right }) =>
    async (event) => {
      const previewWindow = window.open("/preview-content", "_blank");

      const response = await client
        .query(compareContentQuery, {
          action: "COMPARE",
          objects: [{ id: objects[0].id }],
          objectType,
          leftVersionId: left,
          rightVersionId: right,
        })
        .toPromise();

      const previewRequest = parsePreviewRequest(response);
      if (previewRequest?.status === StatusCodes.ACCEPTED) {
        const previewId = previewRequest.data.data[0].previewId;
        await pollAndRender(previewWindow, previewId, true);
      } else {
        previewWindow.document.body.innerHTML =
          "Error Creating Comparison. Please Try Again!";
      }

      setAnchorEl(null);
    };

  return (
    <>
      {previewTypes.length > 0 ? (
        <>
          <Tooltip
            title={`Preview ${objectType}${objects?.length > 1 ? "s" : ""}`}
            aria-label={`Preview ${objectType}${
              objects?.length > 1 ? "s" : ""
            }`}
          >
            <IconButton
              aria-label="Preview"
              color={color}
              disabled={
                !objects?.length ||
                (objectType === "Passage" &&
                  !RIMS_PREVIEW_ENGINES.some((engine) =>
                    previewTypes.includes(engine),
                  ))
              }
              onClick={handlePreviewClick}
              size="small"
            >
              <PreviewMenuIcon />
            </IconButton>
          </Tooltip>
          {/* <iframe src="https://str-common.actdigitalservices.org/" style={{width:500, height:500}}></iframe> */}
          <Menu
            anchorEl={anchorEl}
            id="account-menu"
            open={open}
            onClose={handlePreviewClose}
            onClick={handlePreviewClose}
          >
            {previewTypes.includes("MJ4") && (
              <MenuItem onClick={handlePreviewMenuClose("MJ4")}>
                MathJax4 Preview
              </MenuItem>
            )}
            {previewTypes.includes("RIMS") && (
              <MenuItem onClick={handlePreviewMenuClose("RIMS")}>
                RIMS Preview
              </MenuItem>
            )}
            {previewTypes.includes("RIMS") && compareOptions?.current && (
              <MenuItem
                onClick={handleCompareMenuClose(compareOptions.current)}
              >
                RIMS Compare: Current
              </MenuItem>
            )}
            {previewTypes.includes("RIMS") && compareOptions?.lastTask && (
              <MenuItem
                onClick={handleCompareMenuClose(compareOptions.lastTask)}
              >
                RIMS Compare: Last Task
              </MenuItem>
            )}
            {objectType !== "Passage" && previewTypes.includes("TN8") && (
              <MenuItem onClick={handlePreviewMenuClose("TN8")}>
                TN8 Preview
              </MenuItem>
            )}
            {objectType !== "Passage" && previewTypes.includes("TAO") && (
              <MenuItem onClick={handlePreviewMenuClose("TAO")}>
                TAO Preview
              </MenuItem>
            )}
            {previewTypes.includes("CDE") && (
              <MenuItem
                onClick={openPreviewContentDialog({
                  id: objects[0].id,
                  type: objectType,
                  versionID: objects[0].versionID,
                })}
              >
                CDE Preview
              </MenuItem>
            )}
          </Menu>
          {previewContentDialogProps.open && (
            <PreviewContentDialog {...previewContentDialogProps} />
          )}
        </>
      ) : (
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            width: "100%",
            "& .CBT": { display: display === "PBT" ? "none" : "inline" },
            "& .PBT": { display: display === "CBT" ? "none" : "inline" },
            "& .interaction": {
              border: 0,
              clip: "rect(0 0 0 0)",
              height: "1px",
              margin: "-1px",
              overflow: "hidden",
              padding: "0",
              position: "absolute",
              whiteSpace: "nowrap",
              width: "1px",
            },
            "& del": {
              backgroundColor: "rgba(229,102,134,.35)",
              borderBottom: "3px solid rgba(174,30,66,.35)",
              borderTop: "3px solid rgba(174,30,66,.35)",
              textDecoration: "line-through 3px rgba(87,15,33,.5)",
            },
            "& ins": {
              backgroundColor: "rgba(102,229,119,.35)",
              borderBottom: "3px solid rgba(30,174,49,.35)",
              borderTop: "3px solid rgba(30,174,49,.35)",
              textDecoration: "none",
            },
          }}
        >
          <Toolbar
            disableGutters
            sx={{
              alignItems: "start",
              display: "flex",
              flexDirection: "row",
              justifyContent: "end",
            }}
            variant="dense"
          >
            <ToggleButtonGroup
              exclusive
              onChange={handleCbtPbtToggle}
              size="small"
              value={display}
            >
              <ToggleButton value="CBT">CBT</ToggleButton>
              <ToggleButton value="PBT">PBT</ToggleButton>
            </ToggleButtonGroup>
          </Toolbar>
          {loading ? (
            <Loading title="Loading..." />
          ) : (
            <MathJax dynamic>
              <Box
                dangerouslySetInnerHTML={{
                  __html: content,
                }}
                id={`preview-${objectType}-content`}
              ></Box>
              {objectToPreview.__typename === "Item" &&
                !!objectToPreview.interactions?.length &&
                objectToPreview.interactions?.map((interaction) => (
                  <Interaction
                    interaction={{ ...interaction, stem: content }}
                    key={interaction.identifier}
                    permissions={objectToPreview.permissions || []}
                    suggestions={objectToPreview.contentSuggestions || []}
                  />
                ))}
            </MathJax>
          )}
        </Box>
      )}
    </>
  );
};

const previewContentQuery = `
  query rimsPreviewContent($action: RimsPreviewContentActionsEnum!, $objects: [SimpleObjectInput!]!, $objectType: ObjectTypeEnum!, $previewEngine: PreviewEngineEnum!) {
    rimsPreviewContent(action: $action, objects: $objects, objectType: $objectType, previewEngine: $previewEngine)
  }           
`;

const previewContentStatusDisplayQuery = `
  query rimsPreviewContent($action: RimsPreviewContentActionsEnum!, $previewId: Int) {
    rimsPreviewContent(action: $action, previewId: $previewId)
  }
`;

const compareContentQuery = `
  query rimsPreviewContent($action: RimsPreviewContentActionsEnum!, $objects: [SimpleObjectInput!]!, $objectType: ObjectTypeEnum!, $leftVersionId: String, $rightVersionId: String) {
    rimsPreviewContent(action: $action, objects: $objects, objectType: $objectType, leftVersionId: $leftVersionId, rightVersionId: $rightVersionId)
  }
`;

const Interaction = ({ interaction, permissions }) => {
  const { control, getValues } = useForm();

  const hasInteractionSection = useMemo(() => {
    const stem = document.createElement("div");
    stem.innerHTML = interaction.stem;
    return !!stem.querySelector(
      `section.interaction[data-interaction-name="${interaction.identifier}"]`,
    );
  }, [interaction.stem, interaction.identifier]);

  if (!hasInteractionSection) return null;

  return (
    <Box
      sx={{
        "& del": {
          backgroundColor: "rgba(229,102,134,.35)",
          borderBottom: "3px solid rgba(174,30,66,.35)",
          borderTop: "3px solid rgba(174,30,66,.35)",
          textDecoration: "line-through 3px rgba(87,15,33,.5)",
        },
        "& ins": {
          backgroundColor: "rgba(102,229,119,.35)",
          borderBottom: "3px solid rgba(30,174,49,.35)",
          borderTop: "3px solid rgba(30,174,49,.35)",
          textDecoration: "none",
        },
      }}
    >
      {interaction.__typename === "ChoiceInteraction" &&
        interaction.foils.filter(({ correct }) => correct).length < 2 && (
          <RadioGroup>
            {interaction.foils.map((foil, i) => (
              <FormControlLabel
                control={
                  <>
                    <Radio
                      checked={
                        !permissions.includes("externalReview") && foil.correct
                      }
                    />
                    <Typography>{getLetter(i)}</Typography>
                    <Typography sx={{ marginRight: 1 }}>.</Typography>
                  </>
                }
                key={lettersArray.at(i)}
                label={
                  <Box
                    dangerouslySetInnerHTML={{
                      __html: convertSuggestionsForDisplay(
                        transformMathMlMfenced(foil.content),
                      ),
                    }}
                  />
                }
                value={i}
              />
            ))}
          </RadioGroup>
        )}
      {interaction.__typename === "ChoiceInteraction" &&
        interaction.foils.filter(({ correct }) => correct).length >= 2 && (
          <FormGroup>
            {interaction.foils.map((foil, i) => (
              <FormControlLabel
                control={
                  <>
                    <Checkbox
                      checked={
                        !permissions.includes("externalReview") && foil.correct
                      }
                    />
                    <Typography>{getLetter(i)}</Typography>
                    <Typography sx={{ marginRight: 1 }}>.</Typography>
                  </>
                }
                key={lettersArray.at(i)}
                label={
                  <Box
                    dangerouslySetInnerHTML={{
                      __html: convertSuggestionsForDisplay(
                        transformMathMlMfenced(foil.content),
                      ),
                    }}
                  />
                }
                value={i}
              />
            ))}
          </FormGroup>
        )}
      {interaction.__typename === "ExtendedTextInteraction" && (
        <>
          <ControlledFormInput
            control={control}
            fullWidth
            id={interaction.identifier}
            multiline={true}
            name={interaction.identifier}
            rows={interaction.expectedLines}
            rules={{ maxLength: interaction.expectedLength }}
          />
          <Box
            component="span"
            sx={{ display: "flex", justifyContent: "end", marginTop: "-1em" }}
          >
            <span>
              {`${getValues(`${interaction.identifier}`)?.length || 0}/${
                interaction.expectedLength
              }`}
            </span>
          </Box>
        </>
      )}
    </Box>
  );
};

export { PreviewContent };
